import React, { useMemo } from 'react';
import { useLang } from '../../i18n/LangContext';
import { UserProfile } from '../../types/quiz';
import '../../assets/scss/quiz-editorial.scss';

// Short, punchy bilingual names for the 20 blocker patterns. Kept here (not in
// taxonomy) so the reveal copy stays marketing-tuned and synchronous.
const SHADOW: Record<string, { uk: string; en: string }> = {
    conditional_worth: { uk: 'Цінність, яку треба заслужити', en: 'Worth you have to earn' },
    inner_critic: { uk: 'Внутрішній критик', en: 'The inner critic' },
    comparison_trap: { uk: 'Відчуття «я позаду»', en: 'Feeling behind' },
    significance_loop: { uk: 'Або кращий, або гірший', en: 'Above or below' },
    procrastination_avoidance: { uk: 'Уникання важливого', en: 'Avoiding the important' },
    perfectionistic_delay: { uk: '«Ще не готово»', en: '“Not ready yet”' },
    self_sabotage: { uk: 'Відкат перед фінішем', en: 'Pulling back near the finish' },
    learned_helplessness: { uk: '«Навіщо пробувати?»', en: '“Why try?”' },
    attention_fragmentation: { uk: 'Розсіяна увага', en: 'Scattered attention' },
    autopilot_numbing: { uk: 'Заглушення на автопілоті', en: 'Numbing on autopilot' },
    emotional_reactivity: { uk: 'Емоція за кермом', en: 'Emotion at the wheel' },
    burnout_recovery_resistance: { uk: 'Тиск до обвалу', en: 'Push until you crash' },
    distorted_map: { uk: '«Все мало бути інакше»', en: '“It should be different”' },
    threat_bias: { uk: 'Очікування найгіршого', en: 'Expecting the worst' },
    fixed_mindset: { uk: '«Складно – отже не моє»', en: '“Hard means I can’t”' },
    overthinking: { uk: 'Думаю замість діяти', en: 'Thinking instead of moving' },
    socialized_self: { uk: 'Життя чужими очікуваннями', en: 'Living by others’ expectations' },
    boundary_collapse: { uk: '«Так», коли хочеш «ні»', en: 'Yes when you mean no' },
    isolation_withdrawal: { uk: 'Тягну все сам', en: 'Carrying it alone' },
    achievement_emptiness: { uk: 'Перемога не наповнює', en: 'The win feels empty' },
};

interface Props {
    page: any;
    userProfile: UserProfile;
    pages: any[];
    onNext: () => void;
}

const InsightRevealPage: React.FC<Props> = ({ page, userProfile, pages, onNext }) => {
    const { lang } = useLang();
    const t = (uk: string, en: string) => (lang === 'uk' ? uk : en);

    const { topId, count } = useMemo(() => {
        const tally: Record<string, number> = {};
        (pages || []).forEach((p) => {
            if (!(p.type === 'single_choice' || p.type === 'multiple_choice') || !p.data_field || !p.options) return;
            const ans = (userProfile as any)[p.data_field];
            if (ans == null) return;
            const vals: string[] = Array.isArray(ans) ? ans : [ans];
            p.options
                .filter((o: any) => vals.includes(o.value))
                .forEach((o: any) => {
                    Object.entries(o.scores || {}).forEach(([k, v]: any) => {
                        if (SHADOW[k]) tally[k] = (tally[k] || 0) + (v?.value || 0);
                    });
                });
        });
        let top: string | null = null;
        let max = -1;
        Object.entries(tally).forEach(([k, v]) => {
            if (v > max) { max = v; top = k; }
        });
        return { topId: top, count: Object.keys(tally).length };
    }, [pages, userProfile]);

    const label = topId && SHADOW[topId] ? SHADOW[topId][lang] : null;
    const mode = page.mode || 'early_signal';

    // Graceful fallback to the static page copy if nothing was scored yet.
    if (!label) {
        return (
            <div className="page page--info-content">
                <div className="page__container">
                    <h2 className="page__question">{page.title}</h2>
                    {page.description && <p className="page__subtitle">{page.description}</p>}
                    <div className="page__actions">
                        <button className="page__button page__button--block" onClick={onNext}>{page.buttonText}</button>
                    </div>
                </div>
            </div>
        );
    }

    const accent = '#E66B5B';
    const eyebrow =
        mode === 'profile_forming'
            ? t('ПРОФІЛЬ ФОРМУЄТЬСЯ', 'PROFILE FORMING')
            : t('РАННІЙ СИГНАЛ', 'EARLY SIGNAL');

    return (
        <div className="page page--info-content page--insight-reveal">
            <div className="page__container" style={{ textAlign: 'center' }}>
                <p
                    style={{
                        fontFamily: "'Geist Mono', monospace",
                        fontSize: 11,
                        letterSpacing: '0.22em',
                        color: accent,
                        fontWeight: 700,
                        margin: '0 0 14px',
                    }}
                >
                    {eyebrow}
                </p>

                {mode === 'profile_forming' ? (
                    <>
                        <h2 className="page__question" style={{ marginBottom: 10 }}>
                            {t(`Ми вже оцінили твою поведінку за ${count} патернами.`,
                               `We've already assessed your behavior across ${count} patterns.`)}
                        </h2>
                        <p className="page__subtitle" style={{ marginBottom: 18 }}>
                            {t('Найсильніший сигнал поки – ', 'Strongest signal so far – ')}
                            <strong style={{ color: accent }}>{label}</strong>.
                        </p>
                        <p className="page__subtitle" style={{ marginBottom: 18 }}>
                            {t('Коли бачиш свій головний блокер прямо – і б\'єш точно в нього, а не розпорошуєшся – продуктивність зростає в рази.',
                               'When you see your main blocker directly – and aim straight at it instead of scattering – productivity multiplies.')}
                        </p>
                        <p className="page__subtitle" style={{ color: 'var(--muted, #9a948c)' }}>
                            {t('Залишились маятники – твої повторювані розгойдування між крайнощами. Вони добудують профіль.',
                               'Now the pendulums – your recurring swings between extremes. They complete the profile.')}
                        </p>
                    </>
                ) : (
                    <>
                        <p className="page__subtitle" style={{ marginBottom: 6 }}>
                            {t('Ми вже зчитали твою поведінку. Найсильніше проступає:',
                               'We already read your behavior. What stands out most:')}
                        </p>
                        <h2
                            className="page__question"
                            style={{ color: accent, fontSize: 'clamp(26px, 6vw, 40px)', margin: '0 0 18px' }}
                        >
                            {label}
                        </h2>
                        <p className="page__subtitle" style={{ marginBottom: 18 }}>
                            {t('Цей патерн працює тихо – і саме на таких автоматизмах люди втрачають найбільше часу й енергії, навіть не помічаючи.',
                               'This pattern runs quietly – and these blind automatic loops are where people lose the most time and energy without noticing.')}
                        </p>
                        <p className="page__subtitle" style={{ color: 'var(--muted, #9a948c)', fontSize: 14 }}>
                            {t('Зчитуємо за моделями на основі нобелівських робіт із поведінкової економіки (Канеман, Талер).',
                               'Read through models grounded in Nobel-winning behavioral economics (Kahneman, Thaler).')}
                        </p>
                        <p className="page__subtitle" style={{ marginTop: 16 }}>
                            {t('Профіль формується. Ще кілька питань – і побачиш повну картину та що з цим робити.',
                               'Your profile is forming. A few more questions and you’ll see the full map – and what to do about it.')}
                        </p>
                    </>
                )}

                <button className="page__button" onClick={onNext}>{page.buttonText}</button>
            </div>
        </div>
    );
};

export default InsightRevealPage;
